<template>
    <div>
        <configs-content :loading="$wait.is('fetching.users')">
            <div class="flex justify-between items-start w-full mb-4">
                <h2 class="flex items-center text-3xl mb-4">
                    <nio-icon class="mr-2" name="regular/multiple-neutral-2" />
                    {{ $t('configs.rightPanel.users.subtitles.users') }}
                </h2>

                <div class="flex justify-end items-center">
                    <div class="relative mr-4">
                        <nio-input :placeholder="$t('global.general.search')" v-model="search">
                            <template #after>
                                <nio-icon name="regular/search" />
                            </template>
                        </nio-input>
                    </div>

                    <nio-button
                        color="blue"
                        icon="regular/add"
                        @click="triggerAdd"
                        v-if="showAddButton"
                    >
                        {{ $t('global.general.add') }}
                    </nio-button>
                </div>
            </div>

            <section>
                <div class="flex flex-col w-full border border-gray-200 rounded">
                    <div class="flex items-center p-4 border-b border-gray-200 rounded-t bg-gray-100">
                        <div class="flex-2">
                            {{ $t('configs.rightPanel.users.name') }}
                        </div>

                        <div class="flex-2">
                            {{ $t('configs.rightPanel.users.email') }}
                        </div>

                        <div class="flex-1 flex justify-center">
                            {{ $t('configs.rightPanel.users.active') }}
                        </div>

                        <div class="flex-1 flex justify-end">
                            &nbsp;
                        </div>
                    </div>

                    <div class="flex flex-col items-center w-full h-full">
                        <div class="flex justify-center items-center p-4 w-full h-full" v-if="!users.length">
                            {{ $t('configs.rightPanel.users.noUsers') }}
                        </div>

                        <div
                            class="flex items-center p-4 w-full"
                            :key="user.id"
                            v-for="user in filteredUsers"
                        >
                            <div class="flex-2">
                                {{ user | fullname }}
                            </div>

                            <div class="flex-2">
                                {{ user.email }}
                            </div>

                            <div class="flex-1 flex justify-center">
                                <nio-icon
                                    class="text-lg text-green-500"
                                    name="bold/check-circle-1"
                                    v-if="user.active"
                                />
                                <nio-icon
                                    class="text-lg text-gray-500"
                                    name="bold/remove-circle"
                                    v-else
                                />
                            </div>

                            <div class="flex-1 flex justify-end items-center space-x-2">
                                <button
                                    class="link-gray text-base"
                                    @click="triggerUpdate(user)"
                                    v-tooltip="$t('global.general.edit')"
                                >
                                    <nio-icon name="regular/pencil-write" />
                                </button>
                                <button
                                    class="link-gray text-base"
                                    @click="triggerDelete(user)"
                                    v-tooltip="$t('global.general.delete')"
                                >
                                    <nio-icon name="regular/bin-2-alternate" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </configs-content>

        <configs-footer />

        <user-modal />
        <confirm-modal
            :opened.sync="modals.deleteOpened"
            :title="$t('configs.rightPanel.users.deleteModal.header')"
            @confirm="deleteSelected"
            @deny="userToDelete = null"
        >
            <p slot="message">
                {{ $t('configs.rightPanel.users.deleteModal.message') }}
            </p>
        </confirm-modal>
    </div>
</template>

<script>
    import { mapState, mapActions, mapMutations } from 'vuex';
    import { formatFullName } from '@/utils/index.js';
    import ConfigsContent from '@/components/ConfigsContent.vue';
    import ConfigsFooter from '@/components/ConfigsFooter.vue';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import UserModal from '@/components/Modals/UserModal.vue';

    export default {
        components: {
            ConfigsContent,
            ConfigsFooter,
            ConfirmModal,
            UserModal,
        },

        data() {
            return {
                modals: {
                    deleteOpened: false,
                },
                search: '',
                userToDelete: null,
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('setting', [
                'users',
            ]),

            filteredUsers() {
                const search = this.search.toLowerCase();

                return this.users
                    .filter(user => {
                        if (empty(this.search)) {
                            return true;
                        }

                        // Name
                        if (formatFullName(user).toLowerCase().includes(search)) {
                            return true;
                        }

                        // Email
                        if (user.email.toLowerCase().includes(search)) {
                            return true;
                        }

                        return false;
                    })
                    .sort((a, b) => {
                        const nameA = formatFullName(a);
                        const nameB = formatFullName(b);

                        return nameA.localeCompare(nameB);
                    });
            },

            showAddButton() {
                return empty(this.authUser.related_id);
            },
        },

        watch: {
            'authUser.account.id': {
                immediate: true,
                handler(newValue) {
                    if (!newValue) {
                        return;
                    }

                    this.fetch();
                },
            },
        },

        methods: {
            ...mapActions('setting', [
                'deleteUser',
                'fetchAccountUsers',
            ]),

            ...mapMutations('setting', [
                'setEditingUser',
            ]),

            triggerAdd() {
                this.$modal.open('user');
            },

            triggerUpdate(user) {
                this.setEditingUser(user);
                this.$modal.open('user');
            },

            triggerDelete(user) {
                this.userToDelete = user;
                this.modals.deleteOpened = true;
            },

            async deleteSelected() {
                try {
                    await this.deleteUser(this.userToDelete);

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('configs.rightPanel.users.userModal.deleteMessage'),
                    });
                } catch ({ response }) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('global.errors.unknown'),
                    });
                }
            },

            async fetch() {
                this.$wait.start('fetching.users');

                try {
                    await this.fetchAccountUsers();
                } catch (error) {
                    this.$wait.end('fetching.users');

                    this.$notify.show({
                        type: 'error',
                        message: this.$t('configs.rightPanel.users.errors.fetchingUsers'),
                    });

                    throw error;
                }

                this.$wait.end('fetching.users');
            },
        },
    };
</script>
